import { memo, useCallback, useContext, useState } from "react";
import { Icon } from "@iconify/react";
import RectangleButton from "../Buttons/RectangleButton";
import { HorizontalDividerWithText } from "../Dividers/HorizontalDividerWithText";
import AlertContainer from "../Containers/AlertContainer";
import { Colors } from "../../utils/colors";
import { useTheme } from "../../hooks/useTheme";
import { useLogin } from "../../hooks/useLogin";
import { MixpanelContext } from "../../context/AnalyticsService";
import {
  GoogleAuthProvider,
  firebaseAuth,
  signInWithPopup,
  deleteUser,
} from "../../firebase";
import { getUserDataByEmail } from "../../utils/FirebaseUtils";

type SocialSignInButtonsProps = {
  isSignUp: boolean;
};

// External auth providers: Google sign-in/sign-up, Facebook, Apple, etc.
export const SocialSignInButtons = memo(function SocialSignInButtonsFn(
  props: SocialSignInButtonsProps
) {
  const { isSignUp } = props;
  const { theme } = useTheme();
  const mixpanel = useContext(MixpanelContext);
  const { loginWithCredential } = useLogin();

  const [googleLoading, setGoogleLoading] = useState(false);
  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });

  const showAccountExistsAlert = useCallback(() => {
    setAlertText({
      heading: "An account already exists with this email.",
      subHeading:
        "Please sign in using your existing email and password instead.",
    });
  }, []);

  // With Firebase set to allow multiple accounts per identity provider,
  // Google sign-in on an existing email creates a new user instead of throwing
  // an error. To handle cross-provider conflicts, we check if a user with the
  // same email but different uid exists in Firestore. If so, we alert the user
  // and delete the new orphaned user; if the uid matches, proceed as normal.
  const loginWithGoogle = useCallback(async () => {
    mixpanel.track("Webapp: User Start Login", {
      method: "Google",
      origin: isSignUp ? "Sign Up" : "Sign In",
    });
    try {
      setGoogleLoading(true);
      const provider = new GoogleAuthProvider();
      provider.addScope("email");
      provider.addScope("profile");
      const credential = await signInWithPopup(firebaseAuth, provider);
      if (credential.user !== null) {
        const email = (
          credential.user.providerData.find((p) => p.email)?.email ||
          credential.user.email ||
          ""
        ).toLowerCase();
        if (email) {
          const existingAccount = await getUserDataByEmail(email);
          if (existingAccount && existingAccount.uid !== credential.user.uid) {
            await deleteUser(credential.user).catch(() => {});
            showAccountExistsAlert();
            return;
          }
        }
        loginWithCredential(
          credential,
          true,
          credential.user.displayName || undefined,
          undefined,
          email || undefined
        );
      }
    } catch (error: any) {
      if (error.code === "auth/account-exists-with-different-credential") {
        showAccountExistsAlert();
      } else if (
        error.code !== "auth/popup-closed-by-user" &&
        error.code !== "auth/cancelled-popup-request"
      ) {
        setAlertText({
          heading: "Something went wrong signing in with Google.",
          subHeading: "Please try again.",
        });
      }
    } finally {
      setGoogleLoading(false);
    }
  }, [mixpanel, isSignUp, loginWithCredential, showAccountExistsAlert]);

  return (
    <div className="ColumnNormal" style={{ gap: 14, width: "100%" }}>
      <HorizontalDividerWithText text="or continue with" />
      <RectangleButton
        buttonLabel={<span>Continue with Google</span>}
        onPress={loginWithGoogle}
        altColor={Colors.WHITE}
        altTextColor={Colors.BLACK}
        altPaddingVert={12}
        disabled={googleLoading}
        loading={googleLoading}
        iconLeft={<Icon icon="flat-color-icons:google" height={20} />}
        containerStyles={{ border: `1px solid ${Colors.GRAY4}` }}
      />
      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        theme={theme}
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
    </div>
  );
});
